import './governanceTracking.css';
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Paper,
  CircularProgress,
  TextField,
} from '@mui/material';
import {
  Science as TestIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  BugReport as BugIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const API_DEV_HINT = 'Ensure the metrics portal server is running and ADO is configured (Admin). API: /api/governance-tracking.';

/** Skip Sat/Sun on burndown axis (business-day sprints only). */
function isWeekendIsoDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ""));
  if (!m) return false;
  const wd = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
  return wd === 0 || wd === 6;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Drop TC-coverage phrases from generated summaries for management HTML export. */
function toAdoSummary(text) {
  if (text == null || String(text).trim() === "") return "—";
  const cleaned = String(text)
    .replace(/\s*Test coverage:[^.]*\./gi, "")
    .replace(/\s*No test coverage\./gi, "")
    .replace(/\s*TC:[^.]*\./gi, "")
    .replace(/\s*Coverage:\s*[\d.]+%[^.]*\./gi, "")
    .replace(/\s*\d+\s*test\(s\)(?:,\s*[\d.]+%\s*coverage)?\./gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  return cleaned || "—";
}

/**
 * True only for genuinely reopened items — those that left a completed state and came back.
 * The backend derives this from ADO revision history; an item merely sitting in New/Approved/
 * Active has never been reopened.
 */
function isReopenedRow(r) {
  return r?._reopened === true;
}

/**
 * Row predicates behind each clickable KPI card. The card value and the filtered table
 * are driven from the same entry so a card can never disagree with the rows it opens.
 */
const CARD_FILTERS = {
  tests: {
    label: "Items with Test Cases",
    match: (r) => strCell(r["Tests linked?"]).toLowerCase().startsWith("yes"),
  },
  testplan: {
    label: "Items with Test Plan",
    match: rowHasTestPlan,
  },
  junit: {
    label: "Unit Test Coverage",
    match: (r) => unitTestsBadge(r) === "OK",
  },
  automation: {
    label: "Automation Coverage",
    match: (r) => strCell(r["Automation covered"]).toLowerCase() === "yes",
  },
  reopened: {
    label: "Reopened Count",
    match: isReopenedRow,
  },
  prs: {
    label: "Items with Pull Requests",
    match: (r) => prCountFromRow(r) > 0,
  },
  scope: {
    label: "Scope Creep",
    match: (r) => r?._scope_creep === true,
  },
};

function countByCardFilter(rows, key) {
  const f = CARD_FILTERS[key];
  if (!f) return 0;
  return (rows || []).filter(f.match).length;
}

function pctOfRows(rows, count) {
  const total = (rows || []).length;
  return total > 0 ? Math.round((count / total) * 100) : 0;
}

/** Selected card keeps TR green border; dimmed cards fade when another filter is active. */
function selectableCardSx({ selected, dimmed, accent = '#1A2E22' }) {
  return {
    height: '100%',
    borderRadius: 3,
    background: '#fff',
    color: '#1A2E22',
    border: selected ? `2px solid ${accent}` : '1px solid rgba(26,46,34,0.15)',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
    boxShadow: selected ? '0 4px 14px rgba(26,46,34,0.12)' : 'none',
    opacity: dimmed ? 0.55 : 1,
    transform: selected ? 'translateY(-2px)' : 'none',
    transition: 'box-shadow .2s ease, transform .2s ease, opacity .2s ease, border-color .2s ease',
    '&:hover': {
      boxShadow: '0 4px 14px rgba(26,46,34,0.1)',
      transform: 'translateY(-2px)',
      opacity: 1,
    },
    '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
  };
}

function unitTestsBadge(row) {
  return strCell(row?.["Unit tests covered"] ?? row?.["JUnit covered"]);
}

function computeExportKpis(rows, burndown) {
  const list = rows || [];
  const total = list.length;
  const reopened = list.filter(isReopenedRow).length;
  const withPrs = list.filter((r) => prCountFromRow(r) > 0).length;
  const metrics = burndown?.metrics || {};
  const remaining = metrics.remaining_today != null ? Math.round(Number(metrics.remaining_today) || 0) : null;
  const completed =
    burndown?.totalScope != null && remaining != null
      ? Math.round(Number(burndown.totalScope) - remaining)
      : null;
  const scopeDelta = metrics.scope_delta;
  return {
    total,
    reopened,
    withPrs,
    withPrsPct: total > 0 ? Math.round((withPrs / total) * 100) : 0,
    completed,
    remaining,
    scopeDelta,
  };
}

/**
 * Management sprint health from burndown pace vs capacity.
 * On Track when remaining is at/near the ideal line; Risk when completion lags elapsed sprint progress.
 */
function computeSprintHealth(burndownPoints, burndownMetrics) {
  const points = burndownPoints || [];
  let lastIdx = -1;
  for (let i = 0; i < points.length; i += 1) {
    if (points[i]?.remaining != null && Number.isFinite(Number(points[i].remaining))) lastIdx = i;
  }
  if (lastIdx < 0) {
    return { state: "On Track", detail: "Insufficient burndown data to assess pace." };
  }

  const remaining = Number(points[lastIdx].remaining);
  const ideal = Number(points[lastIdx].ideal);
  const n = points.length;
  const elapsedFrac = n <= 1 ? 1 : lastIdx / (n - 1);
  const expectedPct = elapsedFrac * 100;
  const completedPct = Number(burndownMetrics?.completed_pct);
  const actualPct = Number.isFinite(completedPct) ? completedPct : 0;

  const remainingAboveIdeal =
    Number.isFinite(ideal) && ideal > 0 ? (remaining - ideal) / ideal : 0;
  const completionLag = expectedPct - actualPct;
  // >15% above ideal remaining, or >15 percentage-points behind expected completion → Risk
  const atRisk = remainingAboveIdeal > 0.15 || completionLag > 15;

  if (atRisk) {
    return {
      state: "Risk",
      detail: `Behind pace — ${actualPct.toFixed(1)}% complete vs ~${expectedPct.toFixed(0)}% of sprint elapsed; ${Math.round(remaining)} SP remaining.`,
    };
  }
  return {
    state: "On Track",
    detail: `Pace matches capacity — ${actualPct.toFixed(1)}% complete with ${Math.round(remaining)} SP remaining.`,
  };
}

/** Management-facing burndown metric rows (excludes avg/remaining/scope_delta). */
function buildManagementBurndownMetrics(burndownMetrics, sprintHealth) {
  const rows = [];
  const range = burndownMetrics?.sprint_range;
  if (range != null && String(range).trim() !== "") {
    rows.push({ label: "Sprint range", value: String(range) });
  }
  const pct = Number(burndownMetrics?.completed_pct);
  if (Number.isFinite(pct)) {
    rows.push({ label: "Completed %", value: pct.toFixed(2) });
  }
  if (sprintHealth?.state) {
    rows.push({ label: "Sprint health", value: sprintHealth.state, health: sprintHealth.state });
  }
  return rows;
}

/** Simple SVG line chart for standalone HTML export (no Recharts dependency). */
function buildBurndownSvg(points, width = 860, height = 320) {
  if (!points?.length) return "";
  const pad = { top: 28, right: 24, bottom: 40, left: 48 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const ys = points.flatMap((p) => [p.remaining, p.ideal]).filter((n) => n != null && Number.isFinite(Number(n)));
  const yMax = Math.max(1, ...ys.map(Number));
  const xAt = (i) => pad.left + (points.length <= 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const yAt = (v) => pad.top + innerH - (Number(v) / yMax) * innerH;
  const linePath = (key, color, dash) => {
    const coords = points
      .map((p, i) => (p[key] == null || !Number.isFinite(Number(p[key])) ? null : `${xAt(i)},${yAt(p[key])}`))
      .filter(Boolean);
    if (!coords.length) return "";
    return `<polyline fill="none" stroke="${color}" stroke-width="2.5" ${dash ? 'stroke-dasharray="6 4"' : ""} points="${coords.join(" ")}" />`;
  };
  const labels = points
    .map((p, i) => {
      if (i !== 0 && i !== points.length - 1 && i % Math.ceil(points.length / 6) !== 0) return "";
      return `<text x="${xAt(i)}" y="${height - 12}" text-anchor="middle" font-size="11" fill="#5C6B63">${escapeHtml(p.date)}</text>`;
    })
    .join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" role="img" aria-label="Sprint burndown chart">
  <rect width="100%" height="100%" fill="#FFFFFF"/>
  <line x1="${pad.left}" y1="${pad.top}" x2="${pad.left}" y2="${pad.top + innerH}" stroke="#D5DDD8"/>
  <line x1="${pad.left}" y1="${pad.top + innerH}" x2="${pad.left + innerW}" y2="${pad.top + innerH}" stroke="#D5DDD8"/>
  <text x="12" y="${pad.top + 4}" font-size="11" fill="#5C6B63">${Math.round(yMax)}</text>
  <text x="12" y="${pad.top + innerH}" font-size="11" fill="#5C6B63">0</text>
  ${linePath("ideal", "#8A9A90", true)}
  ${linePath("remaining", "#1A2E22", false)}
  ${labels}
  <g font-size="12" fill="#1A2E22">
    <rect x="${pad.left}" y="6" width="12" height="3" fill="#1A2E22"/>
    <text x="${pad.left + 18}" y="12">Remaining</text>
    <rect x="${pad.left + 110}" y="6" width="12" height="3" fill="#8A9A90"/>
    <text x="${pad.left + 128}" y="12">Ideal</text>
  </g>
</svg>`;
}

function buildKpiTilesHtml(kpis = {}) {
  const scopeUp = kpis.scopeDelta != null && Number(kpis.scopeDelta) > 0;
  const scopeLabel =
    kpis.scopeDelta === undefined || kpis.scopeDelta === null
      ? "N/A"
      : Number(kpis.scopeDelta) > 0
        ? `+${kpis.scopeDelta}`
        : String(kpis.scopeDelta);

  const tiles = [
    {
      tone: "alert",
      title: "Reopened Count",
      value: String(kpis.reopened),
      detail: kpis.reopened > 0 ? "Needs attention" : "All clear",
      foot: kpis.reopened > 0 ? "Monitor" : "Stable",
    },
    {
      tone: "slate",
      title: "Items with Pull Requests",
      value: String(kpis.withPrs),
      detail: `${kpis.withPrsPct}% of total`,
      foot: "Code reviewed",
    },
    {
      tone: "teal",
      title: "Story Points",
      valueHtml:
        kpis.completed == null && kpis.remaining == null
          ? `<div class="kpi-value">N/A</div>`
          : `<div class="kpi-split">
              <div><span class="kpi-split-label">Completed</span><strong>${escapeHtml(kpis.completed ?? 0)}</strong></div>
              <div><span class="kpi-split-label">Remaining</span><strong>${escapeHtml(kpis.remaining ?? 0)}</strong></div>
            </div>`,
      detail: "",
      foot: "",
      customValue: true,
    },
    {
      tone: scopeUp ? "orange" : "green",
      title: "Scope Creep",
      value: scopeLabel,
      detail: "Story points added",
      foot: scopeUp ? "Scope increased" : "Scope stable",
    },
  ];

  return `<div class="kpi-grid">${tiles
    .map((t) => {
      const valueBlock = t.customValue
        ? t.valueHtml
        : `<div class="kpi-value">${escapeHtml(t.value)}</div>`;
      return `<article class="kpi-tile tone-${t.tone}">
      <div class="kpi-title">${escapeHtml(t.title)}</div>
      ${valueBlock}
      ${t.detail ? `<div class="kpi-detail">${escapeHtml(t.detail)}</div>` : ""}
      ${t.foot ? `<div class="kpi-foot">${escapeHtml(t.foot)}</div>` : ""}
    </article>`;
    })
    .join("")}</div>`;
}

function buildSprintHtmlReport({
  sprintLabel,
  teamOrAreaLabel,
  burndownPoints,
  burndownMetrics,
  rows,
  summaries,
  kpis,
  baseUrl,
  project,
}) {
  const generatedAt = new Date().toLocaleString();
  const sprintHealth = computeSprintHealth(burndownPoints, burndownMetrics);
  const managementMetrics = buildManagementBurndownMetrics(burndownMetrics, sprintHealth);
  const metricsHtml = managementMetrics.length
    ? `<ul class="metrics">${managementMetrics
        .map((m) => {
          if (m.health) {
            const tone = m.health === "Risk" ? "risk" : "ok";
            return `<li><span>${escapeHtml(m.label)}</span><strong class="health-pill health-${tone}">${escapeHtml(m.value)}</strong></li>`;
          }
          return `<li><span>${escapeHtml(m.label)}</span><strong>${escapeHtml(m.value)}</strong></li>`;
        })
        .join("")}</ul>
      <p class="health-detail">${escapeHtml(sprintHealth.detail)}</p>`
    : "";
  const chartHtml = burndownPoints?.length
    ? `<div class="chart">${buildBurndownSvg(burndownPoints)}</div>`
    : `<p class="muted">No burndown data for this sprint.</p>`;

  const tableRows = (rows || [])
    .map((r) => {
      const id = strCell(r.ID);
      const openUrl = strCell(r.Open);
      const idHtml = openUrl
        ? `<a href="${escapeHtml(openUrl)}" target="_blank" rel="noreferrer">${escapeHtml(id)}</a>`
        : escapeHtml(id);
      const summary = toAdoSummary(summaries?.[id]);
      const exec = rollupTestExecution(r);
      const planHtml = formatExportTestPlanCell(r, baseUrl, project);
      const executionHtml = exec.outcome ? executionPillHtml(exec.outcome) : "—";
      return `<tr>
        <td class="id-col">${idHtml}</td>
        <td class="summary">${escapeHtml(summary)}</td>
        <td class="plan-col">${planHtml}</td>
        <td class="exec-col">${executionHtml}</td>
      </tr>`;
    })
    .join("");

  const healthBadgeClass = sprintHealth.state === "Risk" ? "badge badge-risk" : "badge badge-ok";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1"/>
  <title>Sprint Summary — ${escapeHtml(sprintLabel)}</title>
  <style>
    :root {
      --tr-green: #1A2E22;
      --tr-green-mid: #2F4A3A;
      --tr-green-soft: #E8EEEA;
      --tr-orange: #E87722;
      --tr-orange-deep: #C45F14;
      --tr-slate: #3D5248;
      --tr-teal: #2A5A4A;
      --tr-alert: #9B3B2E;
      --ink: #1A2E22;
      --muted: #5C6B63;
      --line: #D5DDD8;
      --paper: #F4F7F5;
      --card: #FFFFFF;
    }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      color: var(--ink);
      background: linear-gradient(180deg, #E8EEEA 0%, var(--paper) 180px, var(--paper) 100%);
      font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif;
      line-height: 1.45;
    }
    .page { max-width: 1080px; margin: 0 auto; padding: 32px 28px 56px; }
    .hero {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      gap: 16px;
      margin-bottom: 22px;
      padding-bottom: 16px;
      border-bottom: 3px solid var(--tr-green);
    }
    .hero h1 {
      margin: 0 0 6px;
      font-size: 1.75rem;
      letter-spacing: 0.01em;
      color: var(--tr-green);
    }
    .meta { margin: 0; color: var(--muted); font-size: 0.92rem; }
    .badge {
      color: #fff;
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      padding: 6px 10px;
      border-radius: 999px;
      white-space: nowrap;
    }
    .badge-ok { background: var(--tr-green); }
    .badge-risk { background: var(--tr-orange); }
    h2 {
      margin: 0 0 14px;
      font-size: 1.05rem;
      color: var(--tr-green);
      letter-spacing: 0.02em;
    }
    .card {
      background: var(--card);
      border: 1px solid var(--line);
      border-radius: 12px;
      padding: 18px 20px;
      margin-bottom: 18px;
      box-shadow: 0 1px 2px rgba(26, 46, 34, 0.04);
    }
    .muted { color: var(--muted); }
    .kpi-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 14px;
      margin-bottom: 18px;
    }
    .kpi-tile {
      position: relative;
      overflow: hidden;
      border-radius: 12px;
      padding: 18px 16px 16px;
      color: #fff;
      min-height: 132px;
      box-shadow: 0 8px 18px rgba(26, 46, 34, 0.12);
    }
    .kpi-tile.tone-green { background: linear-gradient(145deg, #1A2E22 0%, #2F4A3A 100%); }
    .kpi-tile.tone-slate { background: linear-gradient(145deg, #3D5248 0%, #2A3B34 100%); }
    .kpi-tile.tone-teal { background: linear-gradient(145deg, #2A5A4A 0%, #1A2E22 100%); }
    .kpi-tile.tone-orange { background: linear-gradient(145deg, #E87722 0%, #C45F14 100%); }
    .kpi-tile.tone-alert { background: linear-gradient(145deg, #9B3B2E 0%, #6F2A21 100%); }
    .kpi-title {
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      opacity: 0.9;
      margin-bottom: 10px;
    }
    .kpi-value {
      font-size: 2.35rem;
      font-weight: 700;
      line-height: 1;
      margin-bottom: 8px;
    }
    .kpi-split {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 8px;
    }
    .kpi-split strong { display: block; font-size: 1.55rem; line-height: 1.1; }
    .kpi-split-label { display: block; font-size: 0.75rem; opacity: 0.85; margin-bottom: 4px; }
    .kpi-detail { font-size: 0.9rem; opacity: 0.92; }
    .kpi-foot { margin-top: 10px; font-size: 0.88rem; opacity: 0.9; }
    .metrics {
      list-style: none;
      padding: 0;
      margin: 0 0 10px;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 8px 20px;
    }
    .metrics li {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      font-size: 0.9rem;
      border-bottom: 1px solid var(--tr-green-soft);
      padding: 6px 0;
    }
    .metrics span { color: var(--muted); }
    .health-pill {
      display: inline-block;
      padding: 3px 10px;
      border-radius: 999px;
      font-size: 0.78rem;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    .health-ok { background: #E8EEEA; color: #1A2E22; }
    .health-risk { background: #FDE8D8; color: #C45F14; }
    .health-detail {
      margin: 0 0 14px;
      color: var(--muted);
      font-size: 0.88rem;
    }
    .chart { overflow-x: auto; }
    table { width: 100%; border-collapse: collapse; font-size: 0.92rem; }
    th, td { text-align: left; padding: 12px 14px; border-bottom: 1px solid var(--line); vertical-align: top; }
    th {
      background: var(--tr-green);
      color: #fff;
      font-weight: 600;
      letter-spacing: 0.02em;
    }
    tr:nth-child(even) td { background: #F7FAF8; }
    td.id-col { width: 120px; white-space: nowrap; font-weight: 700; }
    td.summary { white-space: pre-wrap; word-break: break-word; color: #24362C; }
    td.plan-col { min-width: 180px; max-width: 280px; white-space: normal; word-break: break-word; font-size: 0.86rem; line-height: 1.4; }
    td.exec-col { white-space: nowrap; }
    .plan-muted { color: var(--muted); font-size: 0.82rem; font-weight: 600; }
    .exec-pill {
      display: inline-block;
      padding: 3px 10px;
      border-radius: 999px;
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0.02em;
    }
    .exec-pass { background: #E8EEEA; color: #1A2E22; }
    .exec-fail { background: #FDE8D8; color: #9B3B2E; }
    .exec-block { background: #FFF4D6; color: #8A5A00; }
    .exec-neutral { background: #EEF2F0; color: #5C6B63; }
    a { color: var(--tr-orange-deep); text-decoration: none; font-weight: 700; }
    a:hover { text-decoration: underline; }
    .footer-note {
      margin-top: 8px;
      color: var(--muted);
      font-size: 0.8rem;
      text-align: right;
    }
    @media print {
      body { background: #fff; }
      .kpi-tile { box-shadow: none; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      th, .badge, .health-pill { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .card { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="page">
    <header class="hero">
      <div>
        <h1>Sprint Summary Report</h1>
        <p class="meta">
          Sprint: <strong>${escapeHtml(sprintLabel)}</strong>
          ${teamOrAreaLabel ? ` · Scope: <strong>${escapeHtml(teamOrAreaLabel)}</strong>` : ""}
          · Generated: ${escapeHtml(generatedAt)}
        </p>
      </div>
      <div class="${healthBadgeClass}">${escapeHtml(sprintHealth.state)}</div>
    </header>

    <section>
      ${buildKpiTilesHtml(kpis)}
    </section>

    <section class="card">
      <h2>Burndown (story points)</h2>
      ${metricsHtml}
      ${chartHtml}
    </section>

    <section class="card">
      <h2>ADO items (${(rows || []).length})</h2>
      <table>
        <thead>
          <tr>
            <th>ADO ID</th>
            <th>ADO Summary</th>
            <th>Test Plan</th>
            <th>Execution</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows || `<tr><td colspan="4" class="muted">No work items in this result.</td></tr>`}
        </tbody>
      </table>
    </section>
    <p class="footer-note">HighQ Metrics Portal · Governance sprint export</p>
  </div>
</body>
</html>`;
}

/** All sprints, newest first (by start date). */
function sortSprintsNewestFirst(iterations) {
  return (iterations || [])
    .slice()
    .sort((a, b) => {
      const da = a?.startDate ? new Date(a.startDate).getTime() : 0;
      const db = b?.startDate ? new Date(b.startDate).getTime() : 0;
      return db - da;
    });
}

/** Pick the sprint containing today, or the latest sprint that has already started. */
function defaultSprintPath(iterations) {
  const dated = (iterations || [])
    .filter((it) => it?.startDate && it?.finishDate)
    .slice()
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  if (!dated.length) return iterations?.[0]?.path || "";

  const today = new Date();
  const current = dated.find((it) => {
    const start = new Date(it.startDate);
    const finish = new Date(it.finishDate);
    return today >= start && today <= finish;
  });
  if (current) return current.path;

  for (let i = dated.length - 1; i >= 0; i -= 1) {
    if (new Date(dated[i].startDate) <= today) return dated[i].path;
  }
  return dated[0].path;
}

function formatHttpError(path, status, statusText, serverMsg) {
  if (status === 502 || status === 503 || status === 504) {
    return `HTTP ${status} (${statusText}): dashboard API not reachable — ${API_DEV_HINT}`;
  }
  return serverMsg ?? `${statusText || "Request failed"} (${path})`;
}

async function apiJson(path, init) {
  let r;
  try {
    r = await fetch(path, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers ?? {}),
      },
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    throw new Error(`Cannot reach ${path}: ${msg}. ${API_DEV_HINT}`);
  }
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    const server = j && j.error;
    throw new Error(formatHttpError(path, r.status, r.statusText, server));
  }
  return r.json();
}

function strCell(v) {
  if (v == null) return "";
  return String(v);
}

function testsLinkedClass(v) {
  const c = strCell(v).trim().toLowerCase();
  if (c.startsWith("yes")) return "cell-tone cell-good";
  if (c === "no") return "cell-tone cell-bad";
  return "cell-tone cell-neutral";
}

function unitTestsClass(v) {
  const b = strCell(v).trim();
  if (b === "OK") return "cell-tone cell-good";
  if (b === "Review") return "cell-tone cell-bad";
  return "cell-tone cell-neutral";
}

function automationClass(v) {
  const x = strCell(v).trim().toLowerCase();
  if (x === "yes") return "cell-tone cell-good";
  if (x === "no") return "cell-tone cell-bad";
  return "cell-tone cell-neutral";
}

const EXECUTION_RANK = {
  failed: 50,
  fail: 50,
  error: 50,
  blocked: 40,
  paused: 30,
  inconclusive: 30,
  aborted: 30,
  notexecuted: 20,
  unspecified: 20,
  pending: 20,
  notapplicable: 15,
  passed: 10,
  pass: 10,
};

function executionKey(outcome) {
  return strCell(outcome).trim().toLowerCase().replace(/[\s_-]+/g, "");
}

const NON_PLAN_LABELS = new Set(["not in sprint test plan", "not in test plan", "unavailable"]);

function isRealTestPlanName(name) {
  const value = strCell(name).trim().toLowerCase();
  return Boolean(value) && !NON_PLAN_LABELS.has(value);
}

function rowHasTestPlan(row) {
  const details = Array.isArray(row?._tc_execution_details) ? row._tc_execution_details : [];
  return details.some((detail) => isRealTestPlanName(detail.planName));
}

function testPlanOpenUrl(baseUrl, project, planId) {
  const id = Number(planId);
  if (!baseUrl || !project || !Number.isFinite(id) || id <= 0) return "";
  const root = String(baseUrl).replace(/\/+$/, "");
  return `${root}/${encodeURIComponent(project)}/_testPlans/execute?planId=${id}`;
}

function workItemOpenUrl(baseUrl, project, workItemId) {
  const id = Number(workItemId);
  if (!baseUrl || !project || !Number.isFinite(id) || id <= 0) return "";
  const root = String(baseUrl).replace(/\/+$/, "");
  return `${root}/${encodeURIComponent(project)}/_workitems/edit/${id}`;
}

/**
 * One plan/outcome pair per row for the grid. The worst outcome wins so a single failing
 * test case cannot be hidden behind its passing siblings.
 */
function rollupTestExecution(row) {
  const details = Array.isArray(row?._tc_execution_details) ? row._tc_execution_details : [];
  if (!details.length) return { planEntries: [], unlinkedTcIds: [], outcome: "" };

  const inPlanTcIds = new Set();
  for (const detail of details) {
    if (!isRealTestPlanName(detail.planName)) continue;
    const testCaseId = Number(detail.testCaseId) || 0;
    if (testCaseId > 0) inPlanTcIds.add(testCaseId);
  }

  const planEntries = [];
  const seenPlans = new Set();
  const unlinkedTcIds = [];
  const seenUnlinked = new Set();
  for (const detail of details) {
    const planName = strCell(detail.planName).trim();
    const testCaseId = Number(detail.testCaseId) || 0;

    if (isRealTestPlanName(planName)) {
      const planId = Number(detail.planId) || null;
      const key = planId ? `id:${planId}` : `name:${planName.toLowerCase()}`;
      if (!seenPlans.has(key)) {
        seenPlans.add(key);
        planEntries.push({ planId, planName });
      }
    } else if (testCaseId > 0 && !inPlanTcIds.has(testCaseId) && !seenUnlinked.has(testCaseId)) {
      seenUnlinked.add(testCaseId);
      unlinkedTcIds.push(testCaseId);
    }
  }

  let worst = details[0]?.outcome ?? "";
  for (const d of details) {
    const rank = EXECUTION_RANK[executionKey(d.outcome)] ?? 25;
    if (rank > (EXECUTION_RANK[executionKey(worst)] ?? 25)) worst = d.outcome;
  }
  return {
    planEntries,
    unlinkedTcIds,
    outcome: strCell(worst),
  };
}

function executionClass(outcome) {
  const k = executionKey(outcome);
  if (k === "passed" || k === "pass") return "cell-tone cell-good";
  if (k === "failed" || k === "fail" || k === "error") return "cell-tone cell-bad";
  if (k === "blocked") return "cell-tone cell-warn";
  return "cell-tone cell-neutral";
}

/** HTML export: test plan links + unlinked TC IDs for a work item row. */
function formatExportTestPlanCell(row, baseUrl, project) {
  const exec = rollupTestExecution(row);
  const parts = [];
  for (const entry of exec.planEntries) {
    const url = testPlanOpenUrl(baseUrl, project, entry.planId);
    parts.push(
      url
        ? `<a href="${escapeHtml(url)}" target="_blank" rel="noreferrer">${escapeHtml(entry.planName)}</a>`
        : escapeHtml(entry.planName),
    );
  }
  if (exec.unlinkedTcIds.length) {
    if (parts.length) parts.push("<br/>");
    parts.push('<span class="plan-muted">Not in sprint test plan</span><br/>');
    parts.push(
      exec.unlinkedTcIds
        .map((tcId) => {
          const url = workItemOpenUrl(baseUrl, project, tcId);
          return url
            ? `<a href="${escapeHtml(url)}" target="_blank" rel="noreferrer">${escapeHtml(String(tcId))}</a>`
            : escapeHtml(String(tcId));
        })
        .join(", "),
    );
  }
  return parts.length ? parts.join("") : "—";
}

function executionPillHtml(outcome) {
  const value = strCell(outcome).trim() || "Not Executed";
  const k = executionKey(outcome);
  let cls = "exec-neutral";
  if (k === "passed" || k === "pass") cls = "exec-pass";
  else if (k === "failed" || k === "fail" || k === "error") cls = "exec-fail";
  else if (k === "blocked") cls = "exec-block";
  return `<span class="exec-pill ${cls}">${escapeHtml(value)}</span>`;
}

/**
 * PR count for a row. ADO's linked-PR count is authoritative: it is present even when the
 * GitHub JUnit scan cannot run (no Admin repositories / no token), which only yields
 * `_junit_pr_links` for repos it actually scanned.
 */
function prCountFromRow(r) {
  const adoCount = parseInt(strCell(r?.PRs).trim(), 10);
  if (Number.isFinite(adoCount) && adoCount > 0) return adoCount;
  return junitPrLinksFromRow(r).length;
}

function junitPrLinksFromRow(r) {
  const links = r._junit_pr_links;
  if (!Array.isArray(links)) return [];
  const meta = r._pr_meta || {};
  const out = [];
  for (const pair of links) {
    if (!Array.isArray(pair) || pair.length < 2) continue;
    const label = String(pair[0] ?? "").trim();
    const url = String(pair[1] ?? "").trim();
    if (!url) continue;
    out.push({ label: label || url, url, prMeta: meta[url] || null });
  }
  return out;
}

/** Format age in hours to "Xd Yh" / "Xh Ym" for display. */
function formatAge(ageHours) {
  if (ageHours == null || !Number.isFinite(ageHours)) return null;
  const h = Math.floor(ageHours);
  const m = Math.round((ageHours - h) * 60);
  if (h >= 24) {
    const d = Math.floor(h / 24);
    const rem = h % 24;
    return rem > 0 ? `${d}d ${rem}h` : `${d}d`;
  }
  return m > 0 && h === 0 ? `${m}m` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** PR status badge — state + age chip. Highlights open PRs >24 h in red. */
function PrStatusBadge({ prMeta }) {
  if (!prMeta) return null;
  const { state, ageOpenHours } = prMeta;
  const stale = state === "open" && ageOpenHours != null && ageOpenHours > 24;
  const ageLabel = state === "open" ? formatAge(ageOpenHours) : null;
  if (state === "merged") {
    return <span className="pill pill-pr-merged">✓ Merged</span>;
  }
  if (state === "closed") {
    return <span className="pill pill-pr-closed">Closed</span>;
  }
  // open
  return (
    <span className={`pill ${stale ? "pill-pr-open-stale" : "pill-pr-open"}`}>
      ● Open{ageLabel ? ` · ${ageLabel}` : ""}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Checklist column — is the work item linked to a parent Feature?
// ─────────────────────────────────────────────────────────────────────────────
function ChecklistBadge({ linked }) {
  if (linked) return <span className="pill pill-checklist-ok" title="Linked to a Feature">✔ Feature linked</span>;
  return <span className="pill pill-checklist-warn" title="No parent Feature linked">⚠ No Feature</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Execution Status column — one pill per _tc_execution_details entry
//
// Each entry corresponds to one test case linked to the work item.
// Outcomes are shown individually (comma-separated) so reviewers can see
// exactly which test cases passed, failed, or haven't run yet.
// Hovering a pill shows: "<Test Case Title> · <Plan Name>"
// ─────────────────────────────────────────────────────────────────────────────
const EXEC_OUTCOME_CLS = {
  passed:      'pill-exec-passed',
  pass:        'pill-exec-passed',
  failed:      'pill-exec-failed',
  fail:        'pill-exec-failed',
  error:       'pill-exec-failed',
  blocked:     'pill-exec-blocked',
  notexecuted: 'pill-exec-notrun',
  unspecified: 'pill-exec-notrun',
  pending:     'pill-exec-notrun',
  none:        'pill-exec-notrun',
};

function SingleExecPill({ detail }) {
  const key = (detail.outcome || '').toLowerCase().replace(/[\s_-]+/g, '');
  const cls = EXEC_OUTCOME_CLS[key] || 'pill-exec-notrun';
  // Tooltip: outcome label + test case title + plan name
  const outcomeLabel = detail.outcome || 'Not Run';
  const tip = [outcomeLabel, detail.title, detail.planName].filter(Boolean).join(' · ');
  // Show the TC ID as the pill label so users can identify which TC failed/passed
  const label = detail.testCaseId ? `TC-${detail.testCaseId}` : outcomeLabel;
  return <span className={`pill ${cls}`} title={tip}>{label}</span>;
}

function ExecStatusBadge({ tcExecutionDetails }) {
  if (!tcExecutionDetails || tcExecutionDetails.length === 0) {
    return <span className="pill pill-exec-notests">— No Tests</span>;
  }
  return (
    <span className="exec-status-list">
      {tcExecutionDetails.map((detail, i) => (
        <React.Fragment key={`${detail.testCaseId ?? i}-${detail.planId ?? i}`}>
          {i > 0 && <span className="exec-status-sep">, </span>}
          <SingleExecPill detail={detail} />
        </React.Fragment>
      ))}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Configurable column definitions
// ─────────────────────────────────────────────────────────────────────────────
const ALL_COLUMNS = [
  { key: "id",               label: "ID",                  required: true },
  { key: "type",             label: "Type",                defaultOn: true },
  { key: "title",            label: "Title",               required: true },
  { key: "summary",          label: "Mgmt Summary",        defaultOn: true },
  { key: "state",            label: "State",               defaultOn: true },
  { key: "assignee",         label: "Assignee",            defaultOn: true },
  { key: "subtask_assignee", label: "Sub-task Assignee",   defaultOn: true },
  { key: "sp",               label: "SP",                  defaultOn: true },
  { key: "checklist",        label: "Checklist",           defaultOn: true },
  { key: "exec_status",      label: "Execution Status",    defaultOn: true },
  { key: "tests",            label: "Tests?",              defaultOn: true },
  { key: "tc_cov",           label: "TC cov",              defaultOn: true },
  { key: "junit",            label: "JUnit",               defaultOn: true },
  { key: "auto",             label: "Auto",                defaultOn: false },
  { key: "active",           label: "Active",              defaultOn: false },
  { key: "commits",          label: "Commits",             defaultOn: false },
  { key: "prs",              label: "PRs",                 defaultOn: true },
];

const COL_STORAGE_KEY = "governance_col_config_v1";

function getDefaultColConfig() {
  const m = {};
  for (const col of ALL_COLUMNS) m[col.key] = col.required || col.defaultOn !== false;
  return m;
}

function useColumnConfig() {
  const [visible, setVisible] = React.useState(() => {
    try {
      const stored = localStorage.getItem(COL_STORAGE_KEY);
      if (!stored) return getDefaultColConfig();
      const parsed = JSON.parse(stored);
      const def = getDefaultColConfig();
      return { ...def, ...parsed };
    } catch {
      return getDefaultColConfig();
    }
  });
  const [panelOpen, setPanelOpen] = React.useState(false);

  const toggle = (key) => {
    const col = ALL_COLUMNS.find((c) => c.key === key);
    if (col?.required) return;
    setVisible((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(COL_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const reset = () => {
    const def = getDefaultColConfig();
    setVisible(def);
    try { localStorage.removeItem(COL_STORAGE_KEY); } catch {}
  };

  const isVisible = (key) => visible[key] !== false;
  return { visible, toggle, reset, isVisible, panelOpen, setPanelOpen };
}

/** Gear-button + dropdown panel for toggling columns. */
function ColumnConfigPanel({ colCfg }) {
  const { visible, toggle, reset, panelOpen, setPanelOpen } = colCfg;
  const wrapRef = React.useRef(null);

  // Close the panel when the user clicks anywhere outside the wrapper.
  React.useEffect(() => {
    if (!panelOpen) return;
    function handleOutsideClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setPanelOpen(false);
      }
    }
    // Use mousedown so the panel closes before any other click handlers fire.
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [panelOpen, setPanelOpen]);

  return (
    <div className="col-config-wrap" ref={wrapRef}>
      <button
        type="button"
        className="col-config-btn"
        title="Configure columns"
        onClick={() => setPanelOpen((o) => !o)}
      >
        ⚙ Columns
      </button>
      {panelOpen && (
        <div className="col-config-panel">
          <div className="col-config-panel-header">
            <span>Show / hide columns</span>
            <button type="button" className="col-config-reset" onClick={reset}>Reset</button>
            <button type="button" className="col-config-close" onClick={() => setPanelOpen(false)}>✕</button>
          </div>
          <div className="col-config-list">
            {ALL_COLUMNS.map((col) => (
              <label key={col.key} className={`col-config-item${col.required ? " col-config-item-required" : ""}`}>
                <input
                  type="checkbox"
                  checked={visible[col.key] !== false}
                  disabled={col.required}
                  onChange={() => toggle(col.key)}
                />
                {col.label}
                {col.required && <span className="col-config-req-hint"> (always on)</span>}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Short team label from area path (matches Python `_team_from_area`). */
function teamFromArea(area, project) {
  if (!area) return "";
  const parts = area
    .replace(/\//g, "\\")
    .split("\\")
    .filter((p) => p && p.toLowerCase() !== project.toLowerCase());
  return parts.length ? parts[parts.length - 1] : "";
}

/* ----------------------------------------------------------------------------
 * Hierarchical test-case coverage roll-up (client-side fix).
 *
 * Problem: A parent work item (e.g. Product Backlog Item 924566) usually has NO
 * test cases linked directly to it — the test cases live on its child items
 * (Tasks / Bugs / User Stories). The API computes coverage PER ITEM, so the
 * parent shows a tiny number (e.g. 9%) even though its children are well
 * covered (~60-70%).
 *
 * Fix: rebuild the parent -> child hierarchy from the rows already returned for
 * the sprint, then roll each child's linked-test counts up into its parent so
 * the parent reflects coverage across its whole subtree.
 *
 * Bugs fixed vs. the original implementation:
 *
 *  1. Silent no-op when childrenOf is empty (primary cause of 9% persisting).
 *     OLD: `if (childrenOf.size === 0) return rows` — just silently returned
 *     the raw API values unchanged, with no diagnostic on the affected items.
 *     NEW: container-type items (PBI / Feature / Epic) receive an explanatory
 *     note so developers can see exactly why coverage looks low and what backend
 *     field is needed to activate the rollup.
 *
 *  2. Null-coverage items counted as 100% covered (inflated the aggregate).
 *     OLD: `pct == null ? n : Math.round((pct / 100) * n)` — if a child had
 *     tests linked but no coverage %, ALL its tests were counted as covered.
 *     NEW: items with unknown coverage are excluded from the weighted sum so
 *     they neither inflate nor deflate the aggregate.
 *
 *  3. Per-step Math.round accumulated rounding error.
 *     OLD: rounding was applied to each child's contribution inside the loop.
 *     NEW: exact floats are accumulated; a single Math.round is applied to the
 *     final ratio only.
 *
 * For the rollup to engage, the dashboard API must return a parent reference on
 * each row.  PARENT_FIELD_CANDIDATES lists all accepted field names.  If your
 * API uses a different name, add it to that array — no other change is needed.
 * -------------------------------------------------------------------------- */

// All field names the portal/ADO wrapper might use for the parent work-item ID.
// If yours is missing, add it here — no backend change needed once the field appears.
const PARENT_FIELD_CANDIDATES = [
  // Common portal / custom wrapper names
  "Parent", "ParentId", "ParentID", "Parent ID", "Parent Id",
  "_parent_id", "_parentId", "_parent",
  // ADO REST / WIQL system field aliases
  "System.Parent", "parent_id", "parent",
  "ParentWorkItemId", "parent_work_item_id", "ParentWorkItem",
  "PARENT_ID", "parentId", "parentWorkItemId",
  // Some wrappers serialize as lower-case or snake_case
  "parent_work_item", "parentworkitemid",
];

function rowId(r) {
  return strCell(r?.ID).trim();
}

/** First non-empty parent id found on the row, normalized to a string. */
function rowParentId(r) {
  for (const f of PARENT_FIELD_CANDIDATES) {
    if (r && r[f] != null && String(r[f]).trim() !== "") {
      // Allow ids embedded in URLs / paths: grab the trailing number if present.
      const raw = String(r[f]).trim();
      const m = raw.match(/(\d+)\s*$/);
      return m ? m[1] : raw;
    }
  }
  return "";
}

/** Parse linked-test count from a "Tests linked?" label like "Yes (12)". */
function linkedTestCount(testsLinkedVal) {
  const s = strCell(testsLinkedVal).trim();
  const sl = s.toLowerCase();
  if (sl === "" || sl === "no" || sl === "na") return 0;
  const m = s.match(/\((\d+)\)/);
  if (m) return parseInt(m[1], 10) || 0;
  if (sl.startsWith("yes")) return 1; // "Yes" with no explicit count
  return 0;
}

/**
 * Work item types that can own children (PBIs, Features, Epics, User Stories).
 * When the rollup can't engage (no parent IDs in the API response), these items
 * get a diagnostic note so the developer / user knows why the number looks low.
 */
/** Coerce "Test case coverage" into a 0..100 number, or null for N/A sentinels (-1, -2). */
function coverageNumber(v) {
  if (v == null || v === "") return null;
  const x = typeof v === "number" ? v : parseFloat(String(v));
  if (!Number.isFinite(x) || x < 0) return null;
  return Math.max(0, Math.min(100, x));
}

/** Build id -> row and parentId -> [childRows] maps from the sprint rows. */
function buildHierarchy(rows) {
  const byId = new Map();
  for (const r of rows) {
    const id = rowId(r);
    if (id) byId.set(id, r);
  }
  const childrenOf = new Map();
  for (const r of rows) {
    const pid = rowParentId(r);
    if (pid && byId.has(pid) && pid !== rowId(r)) {
      if (!childrenOf.has(pid)) childrenOf.set(pid, []);
      childrenOf.get(pid).push(r);
    }
  }
  return { byId, childrenOf };
}

/** Collect a row + all descendant rows present in the sprint result (cycle-safe). */
function selfAndDescendants(id, childrenOf, byId) {
  const out = [];
  const seen = new Set();
  const queue = [id];
  while (queue.length) {
    const cur = queue.pop();
    if (seen.has(cur)) continue;
    seen.add(cur);
    const row = byId.get(cur);
    if (row) out.push(row);
    for (const child of childrenOf.get(cur) ?? []) {
      const cid = rowId(child);
      if (cid && !seen.has(cid)) queue.push(cid);
    }
  }
  return out;
}

/**
 * Roll child coverage up into each container item. Returns a NEW rows array.
 * Items that have descendants get an aggregated "Test case coverage",
 * "_tc_cov_badge", "Tests linked?" label, and an explanatory note.
 * Leaves (no children) are returned unchanged.
 *
 * Coverage formula (per item in the subtree):
 *   n   = linkedTestCount  — number of test cases linked to that item
 *   pct = coverageNumber   — % of those test cases that are covered/passing
 *   Only items where BOTH n > 0 AND pct is known contribute to the aggregate.
 *   This avoids the "pct unknown → count all as covered" inflation bug.
 *
 *   aggregate = Σ(n × pct) / Σ(n)   [exact float, rounded once at the end]
 *
 * When no parent linkage is detected in the API response (childrenOf is empty),
 * container-type items (PBI / Feature / Epic) receive a diagnostic note so the
 * developer knows the backend needs to expose a parent ID field to activate the
 * rollup for those items.
 */
function rollUpCoverage(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const { byId, childrenOf } = buildHierarchy(rows);

  // ── No parent linkage at all ──────────────────────────────────────────────
  // Child work items aren't linked in the API response, so coverage reflects
  // only test cases linked directly to each item. Show the item's own coverage
  // note as-is (no technical roll-up diagnostics surfaced to end users).
  if (childrenOf.size === 0) {
    return rows;
  }

  // ── Rollup is possible ────────────────────────────────────────────────────
  return rows.map((r) => {
    const id = rowId(r);
    if (!id || !childrenOf.has(id)) return r; // leaf / no children -> unchanged

    const family = selfAndDescendants(id, childrenOf, byId);

    // Weighted sum: accumulate exact floats; only include items where BOTH
    // the test count AND coverage percentage are known.  Items with linked
    // tests but no coverage data are excluded rather than assumed 100%
    // covered (the old code's "pct == null ? n" path inflated results).
    let totalTests = 0;
    let coveredExact = 0; // float — avoid per-step Math.round accumulation
    const itemBreakdown = []; // for the diagnostic note

    for (const f of family) {
      const n = linkedTestCount(f["Tests linked?"]);
      if (n <= 0) continue;
      const pct = coverageNumber(f["Test case coverage"]);
      if (pct == null) {
        // Tests are linked but coverage % is unknown — skip rather than inflate.
        itemBreakdown.push(`#${rowId(f)} (${n} TC, cov unknown — excluded)`);
        continue;
      }
      totalTests += n;
      coveredExact += (pct / 100) * n; // exact weighted contribution
      itemBreakdown.push(`#${rowId(f)} (${n} TC × ${pct}%)`);
    }

    if (totalTests === 0) return r; // nothing measurable in the subtree

    // Single round at the very end — no intermediate truncation error.
    const aggPct = Math.max(0, Math.min(100, Math.round((coveredExact / totalTests) * 100)));
    const coveredRounded = Math.round(coveredExact);
    const childCount = family.length - 1;

    const rollupNote =
      `Rolled-up coverage: ${aggPct}% across ${family.length} item(s) ` +
      `(this item + ${childCount} descendant${childCount === 1 ? "" : "s"}). ` +
      `${coveredRounded}/${totalTests} effective test cases covered. ` +
      `Breakdown: ${itemBreakdown.join("; ")}.`;

    const priorNote = strCell(r._tc_cov_notes).trim();

    return {
      ...r,
      "Test case coverage": aggPct,
      // "↑" marks the badge as a rolled-up value, distinguishing it from raw API data.
      _tc_cov_badge: `${aggPct}% ↑`,
      "Tests linked?": `Yes (${totalTests} incl. children)`,
      _tc_cov_notes: priorNote ? `${rollupNote} | ${priorNote}` : rollupNote,
      _tc_rollup_applied: true,
    };
  });
}

/** Enrich the dashboard payload with rolled-up coverage before rendering. */
function enrichDashboard(data) {
  if (!data || !Array.isArray(data.rows)) return data;
  try {
    return { ...data, rows: rollUpCoverage(data.rows) };
  } catch {
    // Never let enrichment break the dashboard — fall back to raw API values.
    return data;
  }
}

function DetailRow({ label, children }) {
  return (
    <div className="detail-row">
      <span className="detail-row-label">{label}</span>
      <span className="detail-row-val">{children}</span>
    </div>
  );
}

/** Bold work item type names inside QA / coverage notes (matches Streamlit markdown look). */
function CoverageDetailProse({ text }) {
  const markers = ["Product Backlog Item", "User Story", "Feature", "Bug"].sort((a, b) => b.length - a.length);
  const parts = [];
  let rem = text;
  let k = 0;
  while (rem.length > 0) {
    let bestI = -1;
    let bestM = "";
    for (const m of markers) {
      const i = rem.indexOf(m);
      if (i >= 0 && (bestI < 0 || i < bestI)) {
        bestI = i;
        bestM = m;
      }
    }
    if (bestI < 0) {
      parts.push(<span key={k++}>{rem}</span>);
      break;
    }
    if (bestI > 0) parts.push(<span key={k++}>{rem.slice(0, bestI)}</span>);
    parts.push(<strong key={k++}>{bestM}</strong>);
    rem = rem.slice(bestI + bestM.length);
  }
  return <p className="detail-notes">{parts}</p>;
}

function PillTcTests({ text }) {
  const v = text.trim();
  const vl = v.toLowerCase();
  if (vl === "na") return <span className="pill pill-na">NA</span>;
  if (vl.startsWith("yes")) return <span className="pill pill-yes">{v}</span>;
  if (vl === "no") return <span className="pill pill-no">No</span>;
  return <span className="pill pill-nv">{v || "—"}</span>;
}

function PillPrCount({ n }) {
  if (n > 0) return <span className="pill pill-yes">Yes ({n})</span>;
  return <span className="pill pill-no">No</span>;
}

function PillQaCoverage({ sortVal }) {
  if (sortVal == null || sortVal === "") return <span className="pill pill-nv">—</span>;
  const x = typeof sortVal === "number" ? sortVal : parseFloat(String(sortVal));
  if (!Number.isFinite(x)) return <span className="pill pill-nv">{String(sortVal)}</span>;
  if (Math.abs(x + 2) < 0.001) return <span className="pill pill-na">NA</span>;
  if (Math.abs(x + 1) < 0.001) return <span className="pill pill-nv">N/A</span>;
  const pct = Math.max(0, Math.min(100, Math.round(x)));
  const label = `${pct}%`;
  if (pct === 0) return <span className="pill pill-no">{label}</span>;
  if (pct >= 80) return <span className="pill pill-yes">{label}</span>;
  if (pct >= 40) return <span className="pill pill-warn">{label}</span>;
  return <span className="pill pill-rev">{label}</span>;
}

function PillUnitJUnit({ text }) {
  const v = text.trim();
  if (v === "OK") return <span className="pill pill-ok">OK</span>;
  if (v === "Review") return <span className="pill pill-rev">Review</span>;
  if (v === "NA") return <span className="pill pill-na">NA</span>;
  if (v === "Limit") return <span className="pill pill-lim">Limit</span>;
  if (v === "Off") return <span className="pill pill-nv">Off</span>;
  return <span className="pill pill-nv">{v || "—"}</span>;
}

function PillState({ state }) {
  const s = state.trim();
  const sl = s.toLowerCase();
  if (["done", "closed", "completed", "resolved", "complete", "accepted"].includes(sl)) {
    return <span className="pill pill-state-done">{s}</span>;
  }
  if (["active", "in progress", "committed", "in review"].includes(sl)) {
    return <span className="pill pill-state-active">{s}</span>;
  }
  if (["removed", "cancelled", "canceled"].includes(sl)) {
    return <span className="pill pill-state-removed">{s}</span>;
  }
  return <span className="pill pill-state-neutral">{s || "—"}</span>;
}

function PillExecutionOutcome({ outcome }) {
  const value = strCell(outcome).trim() || "Not Executed";
  const normalized = value.toLowerCase().replace(/[\s_-]+/g, "");
  if (normalized === "passed" || normalized === "pass") {
    return <span className="pill execution-passed">{value}</span>;
  }
  if (normalized === "failed" || normalized === "fail" || normalized === "error") {
    return <span className="pill execution-failed">{value}</span>;
  }
  if (normalized === "blocked") {
    return <span className="pill execution-blocked">{value}</span>;
  }
  return <span className="pill execution-default">{value}</span>;
}

function TestPlanNameLink({ name, planId, baseUrl, project, className = "table-plan-link" }) {
  const label = strCell(name).trim() || "Not in test plan";
  const url = testPlanOpenUrl(baseUrl, project, planId);
  if (url && isRealTestPlanName(label)) {
    return (
      <a className={className} href={url} target="_blank" rel="noreferrer">
        {label}
      </a>
    );
  }
  return null;
}

function TestCaseIdLink({ testCaseId, baseUrl, project, className = "table-tc-link", prefix = "" }) {
  const id = Number(testCaseId);
  if (!Number.isFinite(id) || id <= 0) return null;
  const label = prefix ? `${prefix}${id}` : String(id);
  const url = workItemOpenUrl(baseUrl, project, id);
  if (!url) return <span>{label}</span>;
  return (
    <a className={className} href={url} target="_blank" rel="noreferrer" title={`Open test case ${id} in ADO`}>
      {label}
    </a>
  );
}

function TestPlanCellContent({ exec, baseUrl, project, rowId }) {
  const hasPlans = exec.planEntries.length > 0;
  const hasUnlinked = exec.unlinkedTcIds.length > 0;
  if (!hasPlans && !hasUnlinked) return "—";

  return (
    <>
      {exec.planEntries.map((entry, index) => (
        <React.Fragment key={`${rowId}-plan-${entry.planId || entry.planName}`}>
          {index > 0 ? <br /> : null}
          <TestPlanNameLink
            name={entry.planName}
            planId={entry.planId}
            baseUrl={baseUrl}
            project={project}
          />
        </React.Fragment>
      ))}
      {hasUnlinked ? (
        <>
          {hasPlans ? <br /> : null}
          <span className="plan-unlinked-label">Not in sprint test plan</span>
          <br />
          <span className="plan-unlinked-ids">
            {exec.unlinkedTcIds.map((tcId, index) => (
              <React.Fragment key={`${rowId}-tc-${tcId}`}>
                {index > 0 ? ", " : null}
                <TestCaseIdLink testCaseId={tcId} baseUrl={baseUrl} project={project} />
              </React.Fragment>
            ))}
          </span>
        </>
      ) : null}
    </>
  );
}

function TestExecutionDetails({ details, baseUrl, project }) {
  if (!details.length) {
    return <p className="detail-notes detail-notes-fallback">No linked test cases were found.</p>;
  }
  return (
    <div className="test-execution-list">
      {details.map((detail, index) => (
        <div
          className="test-execution-item"
          key={`${detail.testCaseId}-${detail.planId || "none"}-${index}`}
        >
          <p className="test-execution-title">
            <TestCaseIdLink
              className="detail-tc-link"
              testCaseId={detail.testCaseId}
              baseUrl={baseUrl}
              project={project}
              prefix="TC #"
            />
            {detail.title ? <span> — {strCell(detail.title)}</span> : null}
          </p>
          <DetailRow label="Test plan">
            {isRealTestPlanName(detail.planName) ? (
              <TestPlanNameLink
                className="detail-plan-link"
                name={detail.planName}
                planId={detail.planId}
                baseUrl={baseUrl}
                project={project}
              />
            ) : (
              <span className="detail-muted-val">Not in sprint test plan</span>
            )}
          </DetailRow>
          <DetailRow label="Execution">
            <PillExecutionOutcome outcome={detail.outcome} />
          </DetailRow>
          {Array.isArray(detail.configurations) && detail.configurations.length > 0 ? (
            <DetailRow label="Configuration">
              <span className="detail-muted-val">{detail.configurations.join(", ")}</span>
            </DetailRow>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function WorkItemDetailCard({ row, project, baseUrl }) {
  const idStr = strCell(row.ID);
  const openUrl = strCell(row.Open);
  const title = strCell(row.Title);
  const assignee = strCell(row.Assignee) || "(Unassigned)";
  const team = teamFromArea(strCell(row.Area), project);
  const prs = junitPrLinksFromRow(row);
  const prShow = prs.slice(0, 8);
  const tcNotes = strCell(row._tc_cov_notes).trim();
  const junitNotes = strCell(row._junit_notes).trim();
  const testExecutionDetails = Array.isArray(row._tc_execution_details)
    ? row._tc_execution_details
    : [];
  const sp = row["Story points"];

  const idLink =
    openUrl ? (
      <a className="detail-id-link" href={openUrl} target="_blank" rel="noreferrer">
        #{idStr}
      </a>
    ) : (
      <span className="detail-id-fallback">#{idStr}</span>
    );

  return (
    <article className="detail-card">
      <header className="detail-card-header">
        <h4 className="detail-card-title">{title}</h4>
      </header>
      <div className="detail-card-body">
        <p className="detail-section-label">Overview</p>
        <DetailRow label="Work item">{idLink}</DetailRow>
        <DetailRow label="Type">
          <span className="detail-type-text">{strCell(row.Type)}</span>
        </DetailRow>
        <DetailRow label="State">
          <PillState state={strCell(row.State)} />
        </DetailRow>
        <DetailRow label="Assignee">
          <span className="detail-muted-val">{assignee}</span>
        </DetailRow>
        <DetailRow label="Team">
          <span className="detail-muted-val">{team || "—"}</span>
        </DetailRow>
        <DetailRow label="Story pts">
          <span className="detail-sp-val">{sp !== "" && sp != null ? String(sp) : "—"}</span>
        </DetailRow>

        <p className="detail-section-label">Coverage</p>
        <DetailRow label="Test Case?">
          <PillTcTests text={strCell(row["Tests linked?"])} />
        </DetailRow>
        <DetailRow label="QA coverage">
          <PillQaCoverage sortVal={row["Test case coverage"]} />
        </DetailRow>
        <DetailRow label="Unit coverage">
          <PillUnitJUnit text={unitTestsBadge(row)} />
        </DetailRow>
        <DetailRow label="PR?">
          <PillPrCount n={prs.length} />
        </DetailRow>

        <p className="detail-section-label">Test plan and execution</p>
        <TestExecutionDetails
          details={testExecutionDetails}
          baseUrl={baseUrl}
          project={project}
        />

        <p className="detail-section-label">QA coverage detail</p>
        <CoverageDetailProse
          text={
            tcNotes ||
            "No extended QA coverage narrative was returned for this item. Use the Test Case? and QA coverage rows above."
          }
        />

        <p className="detail-section-label">Unit coverage detail</p>
        <CoverageDetailProse
          text={
            junitNotes ||
            "No extended unit test scan narrative was returned for this item. Use the Unit coverage and PR? rows above."
          }
        />

        <p className="detail-section-label">PRs scanned (unit tests)</p>
        {prs.length > 0 ? (
          <div className="detail-pr-block">
            {prShow.map(({ url, label, prMeta }) => {
              const stale = prMeta?.state === "open" && prMeta?.ageOpenHours > 24;
              return (
                <div key={url} className={`detail-pr-row${stale ? " detail-pr-row-stale" : ""}`}>
                  <a className="detail-pr-link" href={url} target="_blank" rel="noreferrer">
                    {label}
                  </a>
                  {prMeta && <PrStatusBadge prMeta={prMeta} />}
                </div>
              );
            })}
            {prs.length > 8 ? (
              <p className="detail-notes detail-pr-more">+{prs.length - 8} more PR(s) for this item</p>
            ) : null}
          </div>
        ) : (
          <p className="detail-notes detail-notes-fallback">
            No linked pull requests were found for this item, so unit-test coverage could not be assessed.
          </p>
        )}
      </div>
    </article>
  );
}

export default function Governance() {
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState(null);
  const [iterationPath, setIterationPath] = useState("");
  const [areaPath, setAreaPath] = useState("(All areas)");
  const [team, setTeam] = useState("");
  const [maxWi, setMaxWi] = useState(500);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);
  const [dash, setDash] = useState(null);
  const [summaries, setSummaries] = useState({});
  const [loadingSummaries, setLoadingSummaries] = useState(false);
  const [assigneeFilter, setAssigneeFilter] = useState("(All)");
  const [cardFilter, setCardFilter] = useState(null);
  const colCfg = useColumnConfig();

  const selectedIteration = useMemo(
    () => meta?.iterations.find((i) => i.path === iterationPath),
    [meta, iterationPath],
  );

  const useAdminTeams = Boolean(meta?.teams?.length);

  const dashboardQueryBody = () => ({
    sprintName: selectedIteration?.sprintName || selectedIteration?.name || undefined,
    iterationPath,
    team: useAdminTeams ? team || undefined : undefined,
    areaPath: useAdminTeams ? undefined : areaPath,
    maxWorkItems: maxWi,
    sprintStartDate: selectedIteration?.startDate ?? null,
    sprintFinishDate: selectedIteration?.finishDate ?? null,
  });

  // Get unique assignees from dashboard data
  const assignees = useMemo(() => {
    if (!dash || !dash.rows) return [];
    const uniqueAssignees = new Set();
    dash.rows.forEach(row => {
      const assignee = strCell(row.Assignee).trim();
      if (assignee) {
        uniqueAssignees.add(assignee);
      }
    });
    return ["(All)", ...Array.from(uniqueAssignees).sort()];
  }, [dash]);

  // Rows drive the table and the export, narrowed by the assignee dropdown and by
  // whichever KPI card is currently selected. Both filters stack.
  const filteredRows = useMemo(() => {
    let rows = dash?.rows || [];
    if (assigneeFilter !== "(All)") {
      rows = rows.filter((row) => strCell(row.Assignee).trim() === assigneeFilter);
    }
    const card = cardFilter ? CARD_FILTERS[cardFilter] : null;
    if (card) rows = rows.filter(card.match);
    return rows;
  }, [dash, assigneeFilter, cardFilter]);

  const toggleCardFilter = (key) => setCardFilter((prev) => (prev === key ? null : key));

  /** Turns a KPI card into a toggle for its own row filter. */
  const filterCardProps = (key, accent = '#1A2E22') => {
    const selected = cardFilter === key;
    return {
      onClick: () => toggleCardFilter(key),
      onKeyDown: (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        toggleCardFilter(key);
      },
      role: 'button',
      tabIndex: 0,
      'aria-pressed': selected,
      title: selected
        ? 'Showing only these items — click to clear'
        : `Show only ${CARD_FILTERS[key].label} items`,
      sx: selectableCardSx({ selected, dimmed: Boolean(cardFilter) && !selected, accent }),
    };
  };

  useEffect(() => {
    apiJson("/api/governance-tracking/config")
      .then((c) => {
        setCfg(c);
        setMaxWi(c.maxWorkItems);
      })
      .catch((e) => setErr(String(e)));
  }, []);

  useEffect(() => {
    if (!cfg?.ok) return;
    setErr(null);
    apiJson("/api/governance-tracking/meta")
      .then((m) => {
        const allIterations = sortSprintsNewestFirst(m.iterations);
        setMeta({ ...m, iterations: allIterations });
        const defaultIterPath = defaultSprintPath(allIterations);
        setIterationPath((prev) => {
          if (prev && allIterations.some((it) => it.path === prev)) return prev;
          return defaultIterPath;
        });
        if (m.teams?.length) {
          setTeam((prev) => {
            if (prev && m.teams.some((t) => t.id === prev)) return prev;
            return m.teams[0].id;
          });
        }
      })
      .catch((e) => setErr(String(e)));
  }, [cfg?.ok]);

  const runDashboard = async () => {
    if (useAdminTeams && !team) {
      setErr('Select a team before running the dashboard.');
      return;
    }
    setLoading(true);
    setErr(null);
    setNote(null);
    setDash(null);
    setCardFilter(null);
    try {
      const data = await apiJson("/api/governance-tracking/dashboard", {
        method: "POST",
        body: JSON.stringify(dashboardQueryBody()),
      });
      const enriched = enrichDashboard(data);
      setDash(enriched);
      setNote(data.note);
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  };

  const generateSummaries = async () => {
    if (!dash || !dash.rows || dash.rows.length === 0) {
      setErr('No work items to generate summaries for');
      return;
    }

    setLoadingSummaries(true);
    setErr(null);

    try {
      // Get first 20 work item IDs
      const workItemIds = dash.rows.slice(0, 20).map(r => strCell(r.ID)).filter(Boolean);

      const response = await apiJson("/api/governance-tracking/generate-summaries", {
        method: "POST",
        body: JSON.stringify({ workItemIds }),
      });

      if (response.success && response.summaries) {
        setSummaries(response.summaries);
      } else {
        throw new Error('Failed to generate summaries');
      }

    } catch (e) {
      setErr(`Failed to generate summaries: ${String(e)}`);
    } finally {
      setLoadingSummaries(false);
    }
  };

  const downloadExcel = async () => {
    if (useAdminTeams && !team) {
      setErr('Select a team before downloading Excel.');
      return;
    }
    setErr(null);
    try {
      let r;
      try {
        r = await fetch("/api/governance-tracking/export.xlsx", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(dashboardQueryBody()),
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        throw new Error(`Cannot reach governance export: ${msg} ${API_DEV_HINT}`);
      }
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        const server = j && j.error;
        throw new Error(formatHttpError("/api/governance-tracking/export.xlsx", r.status, r.statusText, server));
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sprint-dashboard-${iterationPath.replace(/[^\w.-]+/g, "_").slice(0, 60)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(String(e));
    }
  };

  const burndownChartData = useMemo(() => {
    if (!dash?.burndown?.businessDays?.length) return [];
    const out = [];
    dash.burndown.businessDays.forEach((d, i) => {
      if (isWeekendIsoDate(d)) return;
      out.push({
        date: d.slice(5),
        remaining: dash.burndown.remaining[i],
        ideal: dash.burndown.ideal[i],
      });
    });
    return out;
  }, [dash]);

  const downloadHtmlReport = () => {
    if (!dash) {
      setErr("Run the dashboard first, then export HTML.");
      return;
    }
    setErr(null);
    try {
      const sprintLabel =
        selectedIteration?.sprintName ||
        selectedIteration?.name ||
        iterationPath.replace(/^.+\\/, "") ||
        "Sprint";
      const teamOrAreaLabel = useAdminTeams
        ? ((meta?.teams || []).find((t) => t.id === team)?.displayName || team)
        : (areaPath || "All areas");
      const html = buildSprintHtmlReport({
        sprintLabel,
        teamOrAreaLabel,
        burndownPoints: burndownChartData,
        burndownMetrics: dash.burndown?.metrics || {},
        rows: filteredRows,
        summaries,
        kpis: computeExportKpis(filteredRows, dash.burndown),
        baseUrl: cfg?.baseUrl ?? "",
        project: cfg?.project ?? "",
      });
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sprint-summary-${String(sprintLabel).replace(/[^\w.-]+/g, "_").slice(0, 60)}.html`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(String(e));
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#F8F9FA', minHeight: '100vh' }} className="governance-tracking">
      {cfg && !cfg.ok && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          ADO is not configured in the portal (Admin → organization URL, project, and PAT).
          Governance uses the same Admin ADO credentials as Scrum Metrics — not the .env file.
        </Alert>
      )}

      {cfg?.ok && meta && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#fff' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sprint</InputLabel>
                <Select
                  value={iterationPath}
                  label="Sprint"
                  onChange={(e) => setIterationPath(e.target.value)}
                  disabled={loading}
                >
                  {meta.iterations.map((it) => (
                    <MenuItem key={it.path} value={it.path}>
                      {it.sprintName || it.name || it.path.replace(/^.+\\/, "")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {useAdminTeams ? (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Team</InputLabel>
                  <Select
                    value={team}
                    label="Team"
                    onChange={(e) => setTeam(e.target.value)}
                    disabled={loading}
                  >
                    {meta.teams.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.displayName || t.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Area</InputLabel>
                  <Select
                    value={areaPath}
                    label="Area"
                    onChange={(e) => setAreaPath(e.target.value)}
                    disabled={loading}
                  >
                    {(meta.areas || []).map((a) => (
                      <MenuItem key={a} value={a}>
                        {a}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Max work items"
                value={maxWi}
                onChange={(e) => setMaxWi(parseInt(e.target.value, 10) || 1)}
                disabled={loading}
                inputProps={{ min: 1, max: 5000 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" flexWrap="wrap" gap={1}>
                <Button
                  variant="contained"
                  onClick={() => runDashboard()}
                  disabled={loading || (useAdminTeams && !team)}
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {loading ? 'Running…' : 'Run dashboard'}
                </Button>
                <Button variant="outlined" onClick={() => downloadExcel()} disabled={loading || (useAdminTeams && !team)}>
                  Download Excel
                </Button>
                <Button variant="outlined" onClick={() => downloadHtmlReport()} disabled={loading || !dash}>
                  Export HTML
                </Button>
                {dash?.rows?.length > 0 && (
                  <Button variant="outlined" onClick={() => generateSummaries()} disabled={loadingSummaries || loading}>
                    {loadingSummaries ? 'Generating…' : 'Generate Summaries'}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {err && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }} onClose={() => setErr(null)}>
          {err}
        </Alert>
      )}

      {note && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNote(null)}>
          {note}
        </Alert>
      )}

      {/* Metrics Summary Cards - Health Dashboard Style */}
      {dash && !loading && (
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={3}>
            {/* % of items with TC */}
            <Grid item xs={12} sm={6} md={3}>
              <Card {...filterCardProps('tests', '#2e7d32')}>
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      opacity: 0.9,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    % of items with TC
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 1
                    }}
                  >
                    {pctOfRows(dash.rows, countByCardFilter(dash.rows, 'tests'))}%
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '1rem',
                      mb: 1
                    }}
                  >
                    {countByCardFilter(dash.rows, 'tests')} of {dash.rows.length} items
                  </Typography>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingUpIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '1rem' }}>
                      Linked test cases
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -20,
                      top: -20,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <TestIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* % of items with Test plan */}
            <Grid item xs={12} sm={6} md={3}>
              <Card {...filterCardProps('testplan', '#1A2E22')}>
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      opacity: 0.9,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    % of items with Test plan
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 1
                    }}
                  >
                    {pctOfRows(dash.rows, countByCardFilter(dash.rows, 'testplan'))}%
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '1rem',
                      mb: 1
                    }}
                  >
                    {countByCardFilter(dash.rows, 'testplan')} of {dash.rows.length} items
                  </Typography>
                  <Box display="flex" alignItems="center" mt={2}>
                    <CheckCircleIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '1rem' }}>
                      Sprint test plan mapped
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -20,
                      top: -20,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <TestIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Unit test coverage card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card {...filterCardProps('junit', '#D74B00')}>
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      opacity: 0.9,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    Unit Test Coverage
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 1
                    }}
                  >
                    {countByCardFilter(dash.rows, 'junit')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '1rem',
                      mb: 1
                    }}
                  >
                    {dash.rows.length > 0 ? Math.round((countByCardFilter(dash.rows, 'junit') / dash.rows.length) * 100) : 0}% of total
                  </Typography>
                  <Box display="flex" alignItems="center" mt={2}>
                    <CodeIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '1rem' }}>
                      Unit tests OK
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -20,
                      top: -20,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <CodeIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Items with Automation Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card {...filterCardProps('automation', '#5C6B63')}>
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      opacity: 0.9,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    Automation Coverage
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 1
                    }}
                  >
                    {countByCardFilter(dash.rows, 'automation')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '1rem',
                      mb: 1
                    }}
                  >
                    {dash.rows.length > 0 ? Math.round((countByCardFilter(dash.rows, 'automation') / dash.rows.length) * 100) : 0}% of total
                  </Typography>
                  <Box display="flex" alignItems="center" mt={2}>
                    <CheckCircleIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '1rem' }}>
                      Automated
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -20,
                      top: -20,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Reopened Items Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card {...filterCardProps('reopened', '#D74B00')}>
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      opacity: 0.9,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    Reopened Count
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 1
                    }}
                  >
                    {countByCardFilter(dash.rows, 'reopened')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '1rem',
                      mb: 1
                    }}
                  >
                    Needs attention
                  </Typography>
                  <Box display="flex" alignItems="center" mt={2}>
                    {countByCardFilter(dash.rows, 'reopened') > 0 ? (
                      <TrendingUpIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                    ) : (
                      <TrendingDownIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                    )}
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '1rem' }}>
                      {countByCardFilter(dash.rows, 'reopened') === 0 ? 'All clear' : 'Monitor'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -20,
                      top: -20,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <BugIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Items with PR Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card {...filterCardProps('prs', '#1A2E22')}>
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      opacity: 0.9,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    Items with Pull Requests
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 1
                    }}
                  >
                    {countByCardFilter(dash.rows, 'prs')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '1rem',
                      mb: 1
                    }}
                  >
                    {dash.rows.length > 0
                      ? Math.round((countByCardFilter(dash.rows, 'prs') / dash.rows.length) * 100)
                      : 0}% of total
                  </Typography>
                  <Box display="flex" alignItems="center" mt={2}>
                    <CodeIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '1rem' }}>
                      Code reviewed
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -20,
                      top: -20,
                      opacity: 0.2,
                      zIndex: 0
                    }}
                  >
                    <CodeIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Story Points Card */}
            {dash.burndown?.metrics && (
              <>
                {/* Story Points Card */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)',
                      color: 'white',
                      boxShadow: 3,
                      position: 'relative',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                      <Typography
                        variant="overline"
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          letterSpacing: 0.5,
                          opacity: 0.9,
                          display: 'block',
                          mb: 1
                        }}
                      >
                        Story Points
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                        <Box flex={1} textAlign="left">
                          <Typography
                            variant="body2"
                            sx={{
                              opacity: 0.9,
                              fontSize: '0.85rem',
                              mb: 0.5
                            }}
                          >
                            Completed
                          </Typography>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 700
                            }}
                          >
                            {dash.burndown.totalScope && dash.burndown.metrics['remaining_today'] != null
                              ? Math.round(dash.burndown.totalScope - (dash.burndown.metrics['remaining_today'] || 0))
                              : 0}
                          </Typography>
                        </Box>
                        <Box flex={1} textAlign="right">
                          <Typography
                            variant="body2"
                            sx={{
                              opacity: 0.9,
                              fontSize: '0.85rem',
                              mb: 0.5
                            }}
                          >
                            Remaining
                          </Typography>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 700
                            }}
                          >
                            {Math.round(dash.burndown.metrics['remaining_today'] || 0)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: -20,
                          top: -20,
                          opacity: 0.2,
                          zIndex: 0
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Scope Creep Card */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    {...filterCardProps(
                      'scope',
                      dash.burndown.metrics['scope_delta'] && dash.burndown.metrics['scope_delta'] > 0
                        ? '#D74B00'
                        : '#2e7d32',
                    )}
                  >
                    <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                      <Typography
                        variant="overline"
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          letterSpacing: 0.5,
                          opacity: 0.9,
                          display: 'block',
                          mb: 1
                        }}
                      >
                        Scope Creep
                      </Typography>
                      <Typography
                        variant="h2"
                        sx={{
                          fontWeight: 700,
                          mb: 1
                        }}
                      >
                        {dash.burndown.metrics['scope_delta'] !== undefined 
                          ? (dash.burndown.metrics['scope_delta'] > 0 
                              ? `+${dash.burndown.metrics['scope_delta']}` 
                              : dash.burndown.metrics['scope_delta'])
                          : 'N/A'}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.9,
                          fontSize: '1rem',
                          mb: 1
                        }}
                      >
                        Story points added ({countByCardFilter(dash.rows, 'scope')} item
                        {countByCardFilter(dash.rows, 'scope') === 1 ? '' : 's'})
                      </Typography>
                      <Box display="flex" alignItems="center" mt={2}>
                        {dash.burndown.metrics['scope_delta'] && dash.burndown.metrics['scope_delta'] > 0 ? (
                          <>
                            <TrendingUpIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '1rem' }}>
                              Scope increased
                            </Typography>
                          </>
                        ) : (
                          <>
                            <TrendingDownIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '1rem' }}>
                              Scope stable
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: -20,
                          top: -20,
                          opacity: 0.2,
                          zIndex: 0
                        }}
                      >
                        <TrendingUpIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      )}

      {dash && (
        <>
          {dash.burndown.warn && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Burndown: {dash.burndown.warn}
            </Alert>
          )}

          {burndownChartData.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#fff' }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: '#1A2E22' }}>
                Burndown (story points)
              </Typography>
              <ul className="kv compact">
                {Object.entries(dash.burndown.metrics).map(([k, v]) => (
                  <li key={k}>
                    <span>{k}</span> <strong>{String(v)}</strong>
                  </li>
                ))}
              </ul>
              <div className="chart-wrap">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={burndownChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                    <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#666", fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid rgba(26,46,34,0.15)" }}
                      labelStyle={{ color: "#1A2E22" }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="remaining" name="Remaining" stroke="#1A2E22" dot={false} connectNulls={false} />
                    <Line type="monotone" dataKey="ideal" name="Ideal" stroke="#8A9A90" strokeDasharray="5 5" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Paper>
          )}

          {dash && assignees.length > 1 && (
            <FormControl size="small" sx={{ mb: 2, minWidth: 240 }}>
              <InputLabel>Filter by assignee</InputLabel>
              <Select
                value={assigneeFilter}
                label="Filter by assignee"
                onChange={(e) => setAssigneeFilter(e.target.value)}
                disabled={loading}
              >
                {assignees.map((assignee) => (
                  <MenuItem key={assignee} value={assignee}>
                    {assignee}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#fff' }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, color: '#1A2E22' }}>
              Work items ({filteredRows.length}
              {dash?.rows && dash.rows.length !== filteredRows.length ? ` of ${dash.rows.length}` : ""})
            </Typography>
            {(cardFilter || assigneeFilter !== "(All)") && (
              <div className="active-filters">
                {cardFilter && (
                  <button type="button" className="filter-chip" onClick={() => setCardFilter(null)}>
                    {CARD_FILTERS[cardFilter].label}
                    <span className="filter-chip-x" aria-hidden="true">×</span>
                  </button>
                )}
                {assigneeFilter !== "(All)" && (
                  <button type="button" className="filter-chip" onClick={() => setAssigneeFilter("(All)")}>
                    {assigneeFilter}
                    <span className="filter-chip-x" aria-hidden="true">×</span>
                  </button>
                )}
              </div>
            )}
            <div className="table-toolbar">
              <p className="muted table-legend" style={{ margin: 0, flex: 1 }}>
                <strong>ID</strong> opens the work item in ADO. Green / red / gray cells indicate coverage status.
                Per-item detail cards follow below.
              </p>
              <ColumnConfigPanel colCfg={colCfg} />
            </div>
            <div className="table-wrap wide-table">
              <table className="dash-table">
                <thead>
                  <tr>
                    {colCfg.isVisible("id")               && <th>ID</th>}
                    {colCfg.isVisible("type")             && <th>Type</th>}
                    {colCfg.isVisible("title")            && <th>Title</th>}
                    {colCfg.isVisible("summary")          && <th>Mgmt Summary + TC Coverage</th>}
                    {colCfg.isVisible("state")            && <th>State</th>}
                    {colCfg.isVisible("assignee")         && <th>Assignee</th>}
                    {colCfg.isVisible("subtask_assignee") && <th>Sub-task Assignee</th>}
                    {colCfg.isVisible("sp")               && <th>SP</th>}
                    {colCfg.isVisible("checklist")        && <th title="Is this work item linked to a parent Feature in ADO?">Checklist</th>}
                    {colCfg.isVisible("exec_status")      && <th title="Overall test execution outcome for this work item's linked test cases">Execution Status</th>}
                    {colCfg.isVisible("tests")            && <th>Tests?</th>}
                    {colCfg.isVisible("tc_cov")           && <th>TC cov</th>}
                    {colCfg.isVisible("junit")            && <th>Unit tests</th>}
                    {colCfg.isVisible("auto")             && <th>Auto</th>}
                    {colCfg.isVisible("active")           && <th>Reopen</th>}
                    {colCfg.isVisible("commits")          && <th>Commits</th>}
                    {colCfg.isVisible("prs")              && <th>PRs</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => {
                    const openUrl = strCell(r.Open);
                    const idStr = strCell(r.ID);
                    const exec = rollupTestExecution(r);
                    const prsForRow = junitPrLinksFromRow(r);
                    const hasStalePr = prsForRow.some(
                      (p) => p.prMeta?.state === "open" && p.prMeta?.ageOpenHours > 24,
                    );
                    const subtaskAssignees = Array.isArray(r._subtask_assignees)
                      ? r._subtask_assignees : [];
                    return (
                      <tr key={idStr}>
                        {colCfg.isVisible("id") && (
                          <td className="id-cell">
                            {openUrl ? (
                              <a className="table-id-link" href={openUrl} target="_blank" rel="noreferrer">
                                {idStr}
                              </a>
                            ) : (
                              idStr
                            )}
                          </td>
                        )}
                        {colCfg.isVisible("type") && <td>{strCell(r.Type)}</td>}
                        {colCfg.isVisible("title") && <td className="title">{strCell(r.Title)}</td>}
                        {colCfg.isVisible("summary") && (
                          <td className="summary-cell" title={summaries[idStr] || 'Click "Generate Summaries + TC Coverage" button'}>
                            {loadingSummaries && !summaries[idStr] ? (
                              <span className="summary-loading">Analyzing...</span>
                            ) : summaries[idStr] ? (
                              <span className="summary-text">{summaries[idStr]}</span>
                            ) : (
                              <span className="summary-placeholder">—</span>
                            )}
                          </td>
                        )}
                        {colCfg.isVisible("state") && <td>{strCell(r.State)}</td>}
                        {colCfg.isVisible("assignee") && <td>{strCell(r.Assignee)}</td>}
                        {colCfg.isVisible("subtask_assignee") && (
                          <td className="subtask-assignee-cell">
                            {subtaskAssignees.length > 0
                              ? subtaskAssignees.map((a) => (
                                  <span key={a} className="subtask-assignee-pill">{a}</span>
                                ))
                              : <span className="subtask-assignee-none">—</span>}
                          </td>
                        )}
                        {colCfg.isVisible("sp") && <td>{strCell(r["Story points"])}</td>}
                        {colCfg.isVisible("checklist") && (
                          <td className="nowrap-cell">
                            <ChecklistBadge linked={!!r._feature_linked} />
                          </td>
                        )}
                        {colCfg.isVisible("exec_status") && (
                          <td className="nowrap-cell">
                            <ExecStatusBadge tcExecutionDetails={r._tc_execution_details} />
                          </td>
                        )}
                        {colCfg.isVisible("tests") && (
                          <td className={testsLinkedClass(r["Tests linked?"])}>{strCell(r["Tests linked?"])}</td>
                        )}
                        {colCfg.isVisible("tc_cov") && (
                          <td title={strCell(r._tc_cov_notes)}>{strCell(r._tc_cov_badge)}</td>
                        )}
                        {colCfg.isVisible("junit") && (
                          <td className={unitTestsClass(unitTestsBadge(r))} title={strCell(r._junit_notes)}>
                            {unitTestsBadge(r)}
                          </td>
                        )}
                        {colCfg.isVisible("auto") && (
                          <td className={automationClass(r["Automation covered"])}>
                            {strCell(r["Automation covered"])}
                          </td>
                        )}
                        {colCfg.isVisible("active") && (
                          <td className="nowrap-cell">{strCell(r["Reopen count"])}</td>
                        )}
                        {colCfg.isVisible("commits") && (
                          <td className="nowrap-cell">{strCell(r.Commits)}</td>
                        )}
                        {colCfg.isVisible("prs") && (
                          <td className={`nowrap-cell${hasStalePr ? " pr-cell-stale" : ""}`}>
                            {prsForRow.length > 0 ? (
                              <span>
                                {prsForRow.length}
                                {hasStalePr && (
                                  <span className="pr-stale-dot" title="One or more PRs open >24h">●</span>
                                )}
                              </span>
                            ) : strCell(r.PRs)}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#fff' }} className="item-reference-section">
            <p className="mgmt-section-title">Coverage details</p>
            <p className="muted small item-reference-intro">
              One card per work item: overview, coverage badges, full QA / unit narratives (when returned by the
              API), and PR links — no separate “how TC cov works” panel; read each card below.
            </p>

            <div className="wi-detail-stack">
              {filteredRows.map((r) => (
                <WorkItemDetailCard
                  key={strCell(r.ID)}
                  row={r}
                  project={cfg?.project ?? ""}
                  baseUrl={cfg?.baseUrl ?? ""}
                />
              ))}
            </div>
          </Paper>

          {Object.keys(dash.stateCounts).length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#fff' }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: '#1A2E22' }}>
                States in this result
              </Typography>
              <ul className="chips">
                {Object.entries(dash.stateCounts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([s, n]) => (
                    <li key={s}>
                      <span className="chip-name">{s}</span>
                      <span className="chip-n">{n}</span>
                    </li>
                  ))}
              </ul>
            </Paper>
          )}
        </>
      )}

    </Box>
  );
}
